import { useQuery, useReactiveVar } from '@apollo/client';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { InputBase } from '@mui/material';
import Button from '@mui/material/Button';

import TablePagination from '@mui/material/TablePagination';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from '@tanstack/react-router';
import React, { type ChangeEvent, type MouseEvent, useState } from 'react';

import { AddButton, Card, Table, Title, UsersDeleteModal } from 'src/Administration/Common';
import usersProfiles from 'src/api/profile/usersProfiles';
import { StyledContextMenu } from 'src/components/menus/StyledContextMenu';
import type { ActionItem } from 'src/constants';
import { MENU_GROUPS, USER_ROLES } from 'src/constants';
import viewStateVar from 'src/store/viewState';
import styles from './UsersPageStyles';

const ADMIN_USER_ACTIONS: ActionItem[] = [
  { label: 'Edit', icon: <EditIcon style={{ color: 'currentColor' }} />, group: MENU_GROUPS.MANAGE },
  { label: 'Delete', icon: <DeleteIcon style={{ color: 'currentColor' }} />, group: MENU_GROUPS.DANGER },
];

interface User {
  username: string;
  email: string;
}

interface Profile {
  id: string;
  user: User;
  firstName: string;
  lastName: string;
  role: string;
  phoneNumber: string;
}

interface ProfileNode {
  node: Profile;
}

interface AllProfilesData {
  allProfiles: {
    edges: ProfileNode[];
    totalCount: number;
  };
}

interface TableCellValue {
  value: React.ReactNode;
  display: boolean;
}

type TableRow = TableCellValue[];

interface MoreItemProps {
  id: string;
  eachRowRole: string;
  profileUserName: string;
  refetch?: () => void;
}

interface UserNameProps {
  profileUserName: string;
  id: string;
  eachRowRole: string;
}

interface RoleOption {
  label: string;
  value: string;
}

const MoreItem = ({ id, eachRowRole, profileUserName }: MoreItemProps): React.ReactElement | null => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const classes = styles();
  const viewState = useReactiveVar(viewStateVar);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const handleClick = (event: MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleAction = (action: ActionItem): void => {
    if (action.label === 'Edit') {
      navigate({ to: `/systemadmin/users/edit/${id}` });
    } else if (action.label === 'Delete') {
      viewStateVar({
        ...viewStateVar(),
        usersDeleteDialog: id,
        selectedDeleteUsername: profileUserName,
      });
    }
    setAnchorEl(null);
  };

  return (
    <div>
      {(viewState.signedInUserRole === 'ADM' || eachRowRole !== 'ADM') && (
        <React.Fragment>
          <button
            type="button"
            className={classes.moreVertButton}
            onClick={handleClick}
            style={{ background: 'none', border: 'none', padding: 0, display: 'inline-flex', color: 'inherit' }}
          >
            <MoreVertIcon />
          </button>

          <StyledContextMenu
            actions={ADMIN_USER_ACTIONS}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            onAction={handleAction}
            testIdPrefix="cyp-admin-user-action"
          />
        </React.Fragment>
      )}
    </div>
  );
};

const UserName = ({ profileUserName, id, eachRowRole }: UserNameProps): React.ReactElement => {
  const navigate = useNavigate();
  const viewState = useReactiveVar(viewStateVar);
  const classes = styles();

  const onClickUser = (): void => {
    if (viewState.signedInUserRole === 'ADM' || eachRowRole !== 'ADM')
      navigate({ to: `/systemadmin/users/edit/${id}` });
  };

  return (
    <button
      type="button"
      id="cyp-username"
      className={classes.profileUserName}
      onClick={onClickUser}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left',
      }}
    >
      {profileUserName}
    </button>
  );
};

const convertRows = (data: ProfileNode[], refetch: () => void): TableRow[] => {
  return data.map((a) => [
    { value: a.node.id, display: false },
    {
      value: <UserName eachRowRole={a?.node.role} profileUserName={a.node.user.username} id={a?.node.id} />,
      display: true,
    },
    { value: a.node.user.email, display: true },
    { value: a.node.firstName, display: true },
    { value: a.node.lastName, display: true },
    { value: (USER_ROLES as RoleOption[]).find(({ value }) => value === a.node.role)?.label ?? '', display: true },
    { value: a.node.phoneNumber, display: true },
    {
      value: (
        <MoreItem eachRowRole={a?.node.role} id={a?.node.id} profileUserName={a.node.user.username} refetch={refetch} />
      ),
      display: true,
    },
  ]);
};

interface HeadCell {
  id: string;
  numeric: boolean;
  disablePadding: boolean;
  label: string;
}

const headCells: HeadCell[] = [
  {
    id: 'name',
    numeric: false,
    disablePadding: true,
    label: 'Username',
  },
  {
    id: 'Email',
    numeric: true,
    disablePadding: false,
    label: 'Email',
  },
  {
    id: 'First Name',
    numeric: true,
    disablePadding: false,
    label: 'First Name',
  },
  {
    id: 'Last Name',
    numeric: true,
    disablePadding: false,
    label: 'Last Name',
  },
  {
    id: 'Role',
    numeric: true,
    disablePadding: false,
    label: 'Role',
  },
  {
    id: 'phone',
    numeric: true,
    disablePadding: false,
    label: 'Phone',
  },
  {
    id: '',
    numeric: true,
    disablePadding: false,
    label: '',
  },
];

const TABLE_HEADER_ACTIONS: ActionItem[] = [
  { label: 'Delete', icon: <DeleteIcon style={{ color: 'currentColor' }} />, group: MENU_GROUPS.DANGER },
];

function TableHeaderButton(): React.ReactElement {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const classes = styles();
  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleAction = (): void => {
    viewStateVar({
      ...viewStateVar(),
      usersDeleteDialog: true,
    });
    setAnchorEl(null);
  };

  return (
    <div>
      <Button
        className={classes.tableActionButton}
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        endIcon={<ArrowDropDownIcon />}
      >
        Actions
      </Button>
      <StyledContextMenu
        actions={TABLE_HEADER_ACTIONS}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onAction={handleAction}
        id="basic-menu"
      />
    </div>
  );
}

export default function UsersPage(): React.ReactElement {
  const classes = styles();
  const navigate = useNavigate();
  const viewState = useReactiveVar(viewStateVar);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [searchVal, setSearchVal] = useState('');

  const {
    data: usersList,
    loading,
    fetchMore,
  } = useQuery<AllProfilesData>(usersProfiles, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    variables: {
      first: rowsPerPage,
      last: rowsPerPage,
    },
  });

  const count = usersList?.allProfiles.totalCount ?? 0;

  const gotoAddUserPage = (): void => {
    navigate({ to: '/systemadmin/users/new' });
  };

  const onChangeSelect = (row: TableRow, e: ChangeEvent<HTMLInputElement>): void => {
    const profileUserName = (row[1].value as React.ReactElement<{ profileUserName: string }>)?.props?.profileUserName;
    if (e.target.checked) {
      viewStateVar({
        ...viewStateVar(),
        selectedRowsUserIds: [...viewStateVar().selectedRowsUserIds, row[0].value as string],
        selectedRowsUsernames: [...viewStateVar().selectedRowsUsernames, profileUserName],
      });
    } else {
      viewStateVar({
        ...viewStateVar(),
        selectedRowsUserIds: viewState.selectedRowsUserIds.filter((v) => v !== row[0].value),
        selectedRowsUsernames: viewState.selectedRowsUsernames.filter((v) => v !== profileUserName),
      });
    }
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (_event: MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
    setPage(newPage);
  };

  const fetchMoreData = (howManyDeleted = 0): void => {
    const _count = count - howManyDeleted;
    const last = _count && (page + 1) * rowsPerPage > _count ? _count - page * rowsPerPage : rowsPerPage;

    fetchMore({
      variables: {
        first: (page + 1) * rowsPerPage,
        last: last,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;
        return fetchMoreResult;
      },
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchMoreData changes on every render due to count dependency; only re-fetch when page or rowsPerPage changes
  React.useEffect(() => {
    fetchMoreData();
  }, [page, rowsPerPage]);

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (e.target.value.length === 0) {
      setPage(0);
    } else {
      fetchMore({
        variables: {
          user_Username_Icontains: e.target.value,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previousResult;
          return fetchMoreResult;
        },
      });
    }

    setSearchVal(e.target.value);
  };

  return (
    <Card className={classes.mainCard}>
      <div className={classes.tableHeader}>
        <div className={classes.topSectionContainer}>
          <Title>Users</Title>
          <TablePagination
            sx={{ color: 'text.primary' }}
            rowsPerPageOptions={[25, 50, 100, 250]}
            component="div"
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          {viewState.selectedRowsUserIds.length > 0 ? (
            <TableHeaderButton />
          ) : (
            <Tooltip title="Search">
              <InputBase
                id="cyp-up-quick-search"
                className={classes.input}
                placeholder="Search username"
                style={{
                  width: '300px',
                }}
                onChange={onChangeSearch}
                value={searchVal}
              />
            </Tooltip>
          )}
        </div>

        <div className={classes.addUserButton}>
          <AddButton id="cyp-add-user-btn" onClick={gotoAddUserPage}>
            Add User
          </AddButton>
        </div>
      </div>
      <div className={classes.tableContainer}>
        <Table
          minTableHeight={800}
          rowsPerPage={rowsPerPage}
          loading={loading}
          headCells={headCells}
          onChangeSelect={onChangeSelect}
          rows={usersList ? convertRows(usersList.allProfiles.edges, fetchMoreData) : []}
          selectMode
        />
      </div>
      <UsersDeleteModal />
    </Card>
  );
}
